/**
 * ActiveProblemContext
 *
 * Tracks which problem the user is currently interacting with.
 * Used by FloatingChatButton to know which problem to chat about.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';

export interface ActiveProblem {
  id: string;
  title: string;
}

interface ActiveProblemContextType {
  activeProblem: ActiveProblem | null;
  setActiveProblem: (problem: ActiveProblem | null) => void;
  clearActiveProblem: () => void;
}

const ActiveProblemContext = createContext<ActiveProblemContextType | undefined>(undefined);

interface ActiveProblemProviderProps {
  children: ReactNode;
}

export const ActiveProblemProvider: React.FC<ActiveProblemProviderProps> = ({ children }) => {
  const [activeProblem, setActiveProblemState] = useState<ActiveProblem | null>(null);
  const activeProblemRef = useRef<ActiveProblem | null>(null);

  // Keep ref in sync with state for use in event listener
  useEffect(() => {
    activeProblemRef.current = activeProblem;
  }, [activeProblem]);

  const setActiveProblem = useCallback((problem: ActiveProblem | null) => {
    setActiveProblemState(problem);
  }, []);

  const clearActiveProblem = useCallback(() => {
    setActiveProblemState(null);
  }, []);

  // Global click listener to clear active problem when clicking outside problem cards
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      // Only process if there's an active problem
      if (!activeProblemRef.current) return;

      const target = event.target as HTMLElement;

      // Don't interfere with interactive elements - let their handlers run normally
      const isInteractiveElement = target.closest('button, a, input, select, textarea, [role="button"]') !== null;
      if (isInteractiveElement) return;

      // Check if click is within a problem card
      const isWithinProblemCard = target.closest('.problem-card') !== null;

      // Check if click is on the floating chat button or chat panel (don't clear in these cases)
      const isWithinChatUI =
        target.closest('.floating-chat-button') !== null ||
        target.closest('.github-chat-side-panel') !== null ||
        target.closest('.chat-modal') !== null;

      // Clear active problem if click is outside problem cards and chat UI
      if (!isWithinProblemCard && !isWithinChatUI) {
        setActiveProblemState(null);
      }
    };

    // Use bubbling phase (default) to avoid interfering with capture-phase handlers
    document.addEventListener('click', handleGlobalClick);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  return (
    <ActiveProblemContext.Provider value={{ activeProblem, setActiveProblem, clearActiveProblem }}>
      {children}
    </ActiveProblemContext.Provider>
  );
};

export const useActiveProblem = (): ActiveProblemContextType | null => {
  const context = useContext(ActiveProblemContext);
  // Return null if not within a provider (allows optional usage)
  return context ?? null;
};

export default ActiveProblemContext;
